/*
 * Quick Add for PDP recommendation carousels (Similar styles, Stack it with, ...).
 *
 * Each card renders a [data-quick-add-trigger] button and (for multi-variant
 * products) a <template data-quick-add-template> next to product-item's
 * [data-product-item-json]. This module clones the template into a single
 * shared <dialog>, resolves the variant from the user's option choices and
 * posts to /cart/add.js.
 *
 * The dialog is re-parented to <body> on init: the product section's delegated
 * listeners (theme.js) grab any [data-action="add-to-cart"] click inside the
 * section and would submit the MAIN product form, so no quick-add element may
 * live under the section root while carrying those action names. We use our
 * own data attributes and a document-level listener instead.
 */
(function () {
  'use strict';

  var dialog = null;
  var content = null;
  var state = null;

  var documentClickBound = false;

  document.addEventListener('DOMContentLoaded', init);
  // Theme editor re-renders the section with a fresh dialog; adopt it and
  // drop the stale one we previously moved to <body>.
  document.addEventListener('shopify:section:load', init);

  function init() {
    // With a duplicate id the section's fresh dialog comes first in document
    // order (ours sits at the end of <body>), so getElementById finds it.
    var found = document.getElementById('PdpQuickAddModal');
    if (!found || found === dialog) return;

    if (dialog) dialog.remove();
    dialog = found;
    content = dialog.querySelector('[data-qa-content]');
    state = null;

    document.body.appendChild(dialog);
    dialog.addEventListener('click', onDialogClick);
    // A third-party script on this site patches addEventListener in a way
    // that swallows the native close/cancel events (verified live: even a
    // freshly created <dialog> never fires close). Cleanup therefore runs
    // explicitly through closeDialog(); these listeners are only a backstop.
    dialog.addEventListener('close', cleanup);
    dialog.addEventListener('cancel', cleanup);
    dialog.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        closeDialog();
      }
    });
    // Block scroll gestures on the backdrop (event target is the dialog
    // element itself there). An html/body overflow lock is NOT an option:
    // it resets the scroll position, which blanks the JS-positioned
    // sticky aside on the PDP.
    dialog.addEventListener('wheel', blockBackdropScroll, { passive: false });
    dialog.addEventListener('touchmove', blockBackdropScroll, { passive: false });

    if (!documentClickBound) {
      documentClickBound = true;
      document.addEventListener('click', onDocumentClick);
    }
  }

  function onDocumentClick(event) {
    var trigger = event.target.closest('[data-quick-add-trigger]');
    if (!trigger || trigger.disabled) return;
    event.preventDefault();

    var source = trigger.getAttribute('data-quick-add-source') || 'pdp-recommendation';
    var directVariantId = trigger.getAttribute('data-quick-add-variant');

    if (directVariantId) {
      addDirect(trigger, Number(directVariantId), source);
      return;
    }
    openModal(trigger, source);
  }

  /* ------------------------------------------------------------------ */
  /* Modal lifecycle                                                     */
  /* ------------------------------------------------------------------ */

  function openModal(trigger, source) {
    var cell = trigger.closest('.Carousel__Cell') || trigger.parentElement;
    var template = cell.querySelector('template[data-quick-add-template]');
    var jsonEl = cell.querySelector('[data-product-item-json]');
    if (!template || !jsonEl) return;

    var productData;
    try {
      productData = JSON.parse(jsonEl.textContent).product;
    } catch (err) {
      return;
    }

    // Old browsers without <dialog>: fall back to the product page.
    if (typeof dialog.showModal !== 'function') {
      var link = template.content.querySelector('.PdpQuickAdd__Title');
      if (link) window.location.href = link.getAttribute('href');
      return;
    }

    content.innerHTML = '';
    content.appendChild(template.content.cloneNode(true));

    state = {
      product: productData,
      source: source,
      selected: {},
      optionCount: productData.options.length,
      addButton: content.querySelector('[data-qa-add]'),
      addLabel: content.querySelector('[data-qa-add-label]'),
      errorEl: content.querySelector('.PdpQuickAdd__Error'),
      variant: null,
      busy: false
    };

    // Pre-select options that only have one purchasable value.
    groups().forEach(function (group) {
      var enabled = group.querySelectorAll('[data-qa-option]:not([disabled])');
      if (enabled.length === 1) selectSwatch(enabled[0], true);
    });
    resolveVariant();

    // Pre-fetch colour-variant images so a swatch tap swaps without a blank frame.
    content.querySelectorAll('[data-qa-option][data-image-src]').forEach(function (b) {
      new Image().src = b.getAttribute('data-image-src');
    });

    // Progress bar tracks the slider continuously (listener dies with the
    // cloned DOM on close).
    var slider = content.querySelector('[data-qa-slider]');
    var bar = content.querySelector('[data-qa-progress]');
    if (slider && bar) {
      slider.addEventListener('scroll', function () {
        var max = slider.scrollWidth - slider.clientWidth;
        if (max <= 0) return;
        var count = slider.children.length;
        bar.style.transform = 'translateX(' + (slider.scrollLeft / max) * (count - 1) * 100 + '%)';
      }, { passive: true });
    }

    dialog.showModal();
  }

  function blockBackdropScroll(e) {
    if (e.target === dialog) e.preventDefault();
  }

  function cleanup() {
    state = null;
    content.innerHTML = '';
  }

  function closeDialog() {
    cleanup();
    if (dialog.open) dialog.close();
  }

  function onDialogClick(event) {
    if (event.target === dialog) {
      closeDialog();
      return;
    }
    if (event.target.closest('[data-qa-close]')) {
      closeDialog();
      return;
    }
    var slideNav = event.target.closest('[data-qa-slide-prev], [data-qa-slide-next]');
    if (slideNav) {
      var slider = content.querySelector('[data-qa-slider]');
      if (slider) {
        var dir = slideNav.hasAttribute('data-qa-slide-next') ? 1 : -1;
        // Smooth is fine here — unlike the swatch image swap, an arrow click
        // doesn't reflow the layout, so the animation survives.
        slider.scrollBy({ left: dir * slider.clientWidth, behavior: 'smooth' });
      }
      return;
    }
    var swatch = event.target.closest('[data-qa-option]');
    if (swatch && !swatch.disabled && state) {
      selectSwatch(swatch, false);
      resolveVariant();
      return;
    }
    var addButton = event.target.closest('[data-qa-add]');
    if (addButton && state && state.variant && !state.busy) {
      addFromModal();
    }
  }

  /* ------------------------------------------------------------------ */
  /* Option selection                                                    */
  /* ------------------------------------------------------------------ */

  function groups() {
    return Array.prototype.slice.call(content.querySelectorAll('[data-qa-option-group]'));
  }

  function selectSwatch(swatch, silent) {
    var group = swatch.closest('[data-qa-option-group]');
    group.querySelectorAll('.is-active').forEach(function (el) {
      el.classList.remove('is-active');
      el.setAttribute('aria-pressed', 'false');
    });
    swatch.classList.add('is-active');
    swatch.setAttribute('aria-pressed', 'true');
    state.selected[swatch.getAttribute('data-position')] = swatch.getAttribute('data-value');
    // "Metal Color: Platinum" — mirror the main form's selected-value label.
    var option = group.closest('.PdpQuickAdd__Option');
    var selectedLabel = option && option.querySelector('[data-qa-selected]');
    if (selectedLabel) selectedLabel.textContent = swatch.getAttribute('data-value');
    showVariantImage(swatch);
    if (!silent) hideError();
  }

  // Colour swatches carry their variant image; scroll the slider to it
  // (swapping the first slide in when the image isn't among the slides).
  function showVariantImage(swatch) {
    var imageId = swatch.getAttribute('data-image-id');
    if (!imageId) return;
    var slider = content.querySelector('[data-qa-slider]');
    if (!slider) return;
    var slides = slider.children;
    var target = -1;
    for (var i = 0; i < slides.length; i++) {
      if (slides[i].getAttribute('data-image-id') === imageId) {
        target = i;
        break;
      }
    }
    if (target === -1) {
      var firstImg = slides[0] && slides[0].querySelector('img');
      var src = swatch.getAttribute('data-image-src');
      if (!firstImg || !src) return;
      firstImg.src = src;
      slides[0].setAttribute('data-image-id', imageId);
      target = 0;
    }
    slider.scrollLeft = target * slider.clientWidth;
  }

  function matchesSelection(variant, exceptPosition) {
    for (var position in state.selected) {
      if (Number(position) === exceptPosition) continue;
      if (variant['option' + position] !== state.selected[position]) return false;
    }
    return true;
  }

  function resolveVariant() {
    var product = state.product;
    var chosen = Object.keys(state.selected).length;

    // Grey out values that have no purchasable variant given the other choices.
    groups().forEach(function (group) {
      var position = Number(group.getAttribute('data-option-position'));
      group.querySelectorAll('[data-qa-option]').forEach(function (swatch) {
        var value = swatch.getAttribute('data-value');
        var purchasable = product.variants.some(function (variant) {
          return variant.available &&
            variant['option' + position] === value &&
            matchesSelection(variant, position);
        });
        swatch.disabled = !purchasable;
      });
    });

    if (chosen < state.optionCount) {
      state.variant = null;
      setAddButton(false, 'Select ' + pendingOptionNames().join(' / '));
      return;
    }

    var variant = null;
    for (var i = 0; i < product.variants.length; i++) {
      if (matchesSelection(product.variants[i], -1)) {
        variant = product.variants[i];
        break;
      }
    }

    if (!variant || !variant.available) {
      state.variant = null;
      setAddButton(false, 'Out of Stock');
      return;
    }

    state.variant = variant;
    showPriceFor(variant.id);
    setAddButton(true, 'Add to Cart');
  }

  function pendingOptionNames() {
    var names = [];
    state.product.options.forEach(function (name, index) {
      if (!state.selected[String(index + 1)]) names.push(name.toLowerCase());
    });
    return names.length ? names : ['options'];
  }

  function showPriceFor(variantId) {
    content.querySelectorAll('[data-qa-price]').forEach(function (el) {
      el.hidden = Number(el.getAttribute('data-variant-id')) !== variantId;
    });
  }

  function setAddButton(enabled, label) {
    if (!state.addButton) return;
    state.addButton.disabled = !enabled;
    if (state.addLabel) state.addLabel.textContent = label;
  }

  /* ------------------------------------------------------------------ */
  /* Cart                                                                */
  /* ------------------------------------------------------------------ */

  function addFromModal() {
    var session = state; // survives dialog close/reopen while the POST is in flight
    var variant = session.variant;
    var source = session.source;
    session.busy = true;
    setAddButton(false, 'Adding...');
    hideError();

    postToCart(variant.id, source).then(function (result) {
      if (result.ok) {
        // The item IS in the cart even if the dialog was closed mid-flight:
        // always fire the events or the drawer/header count go stale.
        trackAdd(variant, source);
        closeDialog();
        notifyCart(variant);
        return;
      }
      session.busy = false;
      // Only touch the UI if this dialog session is still the live one.
      if (state === session) {
        setAddButton(true, 'Add to Cart');
        showError(result.description);
      }
    });
  }

  function addDirect(trigger, variantId, source) {
    // aria-disabled instead of disabled keeps keyboard focus on the button;
    // the "+" icon button signals busy via opacity, not a text swap.
    if (trigger.getAttribute('aria-disabled') === 'true') return;
    trigger.setAttribute('aria-disabled', 'true');
    trigger.classList.add('is-busy');

    postToCart(variantId, source).then(function (result) {
      trigger.removeAttribute('aria-disabled');
      trigger.classList.remove('is-busy');
      if (result.ok) {
        trackAdd({ id: variantId }, source);
        notifyCart({ id: variantId, quantity: 1 });
      }
    });
  }

  function postToCart(variantId, source) {
    document.dispatchEvent(new CustomEvent('theme:loading:start'));
    return fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({
        items: [{
          id: variantId,
          quantity: 1,
          properties: { _source: 'pdp-' + source }
        }]
      })
    }).then(function (response) {
      document.dispatchEvent(new CustomEvent('theme:loading:end'));
      if (response.ok) return { ok: true };
      return response.json().then(function (data) {
        return { ok: false, description: data.description || 'Something went wrong. Please try again.' };
      }).catch(function () {
        return { ok: false, description: 'Something went wrong. Please try again.' };
      });
    }).catch(function () {
      document.dispatchEvent(new CustomEvent('theme:loading:end'));
      return { ok: false, description: 'Something went wrong. Please try again.' };
    });
  }

  function notifyCart(variant) {
    var dispatch = function () {
      document.dispatchEvent(new CustomEvent('product:added', {
        bubbles: true,
        detail: { variant: variant, quantity: 1 }
      }));
    };
    if (typeof Shopify !== 'undefined' && typeof Shopify.getCart === 'function') {
      Shopify.getCart(dispatch);
    } else {
      dispatch();
    }
  }

  function trackAdd(variant, source) {
    if (!window.dataLayer) return;
    window.dataLayer.push({
      event: 'pdp_quick_add',
      quick_add_source: source,
      variant_id: variant.id,
      price: typeof variant.price === 'number' ? variant.price / 100 : undefined
    });
  }

  /* ------------------------------------------------------------------ */
  /* Errors                                                              */
  /* ------------------------------------------------------------------ */

  function showError(message) {
    if (!state || !state.errorEl) return;
    state.errorEl.textContent = message;
    state.errorEl.hidden = false;
  }

  function hideError() {
    if (!state || !state.errorEl) return;
    state.errorEl.hidden = true;
    state.errorEl.textContent = '';
  }
})();
